import { useEffect, useState, type FormEvent } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Layout from '@/components/Layout'
import { createWanted, updateWanted, fetchWantedOrder } from '@/lib/api'

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

export default function WantedForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(isEdit)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    title: '', slug: '', brand: '', model: '', part: '', budget_aed: '',
    zone: 'sharjah', buyer: '', whatsapp: '', is_active: true,
  })

  useEffect(() => {
    if (isEdit) {
      fetchWantedOrder(id!).then(w => {
        if (w) {
          setForm({
            title: w.title, slug: w.slug, brand: w.brand ?? '', model: w.model ?? '', part: w.part ?? '',
            budget_aed: w.budget_aed != null ? String(w.budget_aed) : '', zone: w.zone ?? 'sharjah',
            buyer: w.buyer ?? '', whatsapp: w.whatsapp ?? '', is_active: w.is_active,
          })
        }
        setLoading(false)
      }).catch(() => setLoading(false))
    }
  }, [id, isEdit])

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm(f => ({ ...f, [key]: value }))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setSaving(true)
    try {
      const payload = {
        title: form.title,
        slug: form.slug || slugify(form.title),
        brand: form.brand || null,
        model: form.model || null,
        part: form.part || null,
        budget_aed: form.budget_aed ? Number(form.budget_aed) : null,
        zone: form.zone || null,
        buyer: form.buyer || null,
        whatsapp: form.whatsapp || null,
        is_active: form.is_active,
      }
      if (isEdit) await updateWanted(id!, payload)
      else await createWanted(payload)
      navigate('/wanted')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save wanted order')
    } finally {
      setSaving(false)
    }
  }

  const input = 'w-full px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent'
  const label = 'block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5'

  return (
    <Layout title={isEdit ? 'Edit Wanted Order' : 'Add Wanted Order'}>
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl border border-surface-200 shadow-card p-6 md:p-8">
          {loading ? (
            <p className="text-slate-400 text-sm py-8 text-center">Loading...</p>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className={label}>Title *</label>
                <input className={input} value={form.title} onChange={e => set('title', e.target.value)} required
                  placeholder="WANTED: Toyota Hilux 2.7L 2TR-FE engine" />
              </div>
              <div>
                <label className={label}>Slug</label>
                <input className={input} value={form.slug} onChange={e => set('slug', slugify(e.target.value))} placeholder="auto-generated" />
              </div>
              <div>
                <label className={label}>Zone</label>
                <select className={input} value={form.zone} onChange={e => set('zone', e.target.value)}>
                  <option value="sharjah">Sharjah</option>
                  <option value="ajman">Ajman</option>
                  <option value="al-quoz">Dubai · Al Quoz</option>
                </select>
              </div>
              <div>
                <label className={label}>Brand</label>
                <input className={input} value={form.brand} onChange={e => set('brand', e.target.value)} placeholder="Toyota" />
              </div>
              <div>
                <label className={label}>Model</label>
                <input className={input} value={form.model} onChange={e => set('model', e.target.value)} placeholder="Hilux 2005–2015" />
              </div>
              <div>
                <label className={label}>Part</label>
                <input className={input} value={form.part} onChange={e => set('part', e.target.value)} placeholder="Engine (2TR-FE)" />
              </div>
              <div>
                <label className={label}>Budget (AED)</label>
                <input type="number" min="0" className={input} value={form.budget_aed} onChange={e => set('budget_aed', e.target.value)} placeholder="6000" />
              </div>
              <div>
                <label className={label}>Buyer</label>
                <input className={input} value={form.buyer} onChange={e => set('buyer', e.target.value)} placeholder="Workshop · Deira, Dubai" />
              </div>
              <div>
                <label className={label}>WhatsApp</label>
                <input className={input} value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} placeholder="971501112233" />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer pt-1">
              <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)}
                className="w-4 h-4 rounded border-surface-300 text-brand-600 focus:ring-brand-500" />
              Active (visible)
            </label>

            <div className="flex gap-3 pt-4 border-t border-surface-100">
              <button type="submit" disabled={saving}
                className="bg-brand-600 text-white font-semibold text-sm px-6 py-2.5 rounded-xl hover:bg-brand-700 disabled:opacity-50 transition-colors">
                {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Wanted Order'}
              </button>
              <button type="button" onClick={() => navigate('/wanted')}
                className="bg-white border border-surface-200 text-slate-600 font-semibold text-sm px-6 py-2.5 rounded-xl hover:bg-surface-50 transition-colors">
                Cancel
              </button>
            </div>
          </form>
          )}
        </div>
      </div>
    </Layout>
  )
}
